import { Fragment, PureComponent, useState } from 'react';
import { DatePicker, Modal, notification, Select } from 'antd';
import { getSelectedClinicId } from '../store/idbHandler';
import { expires_in, start_from, record_types, purpose_types } from '../constants';
import moment from 'moment';
import { DocProfileResponse } from '../api/fetchDoctorProfile';
import createConsentForNDHMRecords from '../api/createConsentForNDHMRecords';
import { bottonArrowIcon, filePenIcon, rightArrowIcon, tickIcon } from '../components/common/Icons';
import { Patient } from '../store/types/patient';
import { track } from '../tracking';
import { his } from './history';
import { GO_TO_PATIENTS_DETAILS } from '../routes/paths';

type Props = {
    plId: string;
    isModalOpen: boolean;
    closeMe: () => void;
    setActiveTab?: (activeTab: 'my-records' | 'request') => void;
    patientDirectory: {
        id?: string;
        abha?: {
            id?: string;
            records?: number;
        };
    };
    doctorprofile: DocProfileResponse | undefined;
    onRefresh?: () => void;
};

type StartsFrom = { isoString: string; shownDate: string };
type Expiry = StartsFrom;

type State = {
    startsFrom: StartsFrom | undefined;
    expiry: Expiry | undefined;
    selectedRecordTypes: string[] | undefined;
    purpose: string | undefined;
    requestCompleted: boolean;
    isLoading: boolean;
};

const filePen = filePenIcon('fill-white w-20 h-20');

const getSubtractedMonthsIsoString = (
    value: number,
    unit: 'month' | 'months' | 'day' | 'days' | 'week',
) => {
    return moment().subtract(value, unit).utc(false).startOf('date').toISOString();
};

const getAddedMonthsIsoString = (
    value: number,
    unit: 'month' | 'months' | 'day' | 'days' | 'week',
) => {
    return moment().add(value, unit).utc(false).startOf('date').toISOString();
};

class RequestModalRequests extends PureComponent<Props, State> {
    state: State = {
        startsFrom: {
            isoString: getSubtractedMonthsIsoString(6, 'months'),
            shownDate: `Last 6 months`,
        },
        expiry: {
            isoString: getAddedMonthsIsoString(6, 'months'),
            shownDate: `6 months`,
        },
        selectedRecordTypes: record_types,
        purpose: purpose_types[0].value,
        requestCompleted: false,
        isLoading: false,
    };

    onRequestMedicalRecords = async () => {
        const { startsFrom, expiry, purpose, selectedRecordTypes } = this.state;
        const { patientDirectory, doctorprofile, closeMe } = this.props;
        const clinicId = getSelectedClinicId();
        this.setState({ isLoading: true });

        const data = {
            patient: {
                oid: patientDirectory?.id || '',
                health_id: patientDirectory?.abha?.id || '',
            },
            hiu: {
                clinic_id: clinicId || '',
                d_oid: doctorprofile?.loginmeta?.docid || '',
                requester: {
                    name: [
                        doctorprofile?.profile?.personal.name.f || '',
                        doctorprofile?.profile?.personal.name.l || '',
                    ]
                        .filter(Boolean)
                        .join(' '),
                },
            },
            period: {
                from: startsFrom?.isoString || '',
                to: moment(new Date()).toISOString() || '',
                expiry: expiry?.isoString || '',
            },
            record_types: selectedRecordTypes || [],
            purpose: purpose || '',
        };

        try {
            const consentData = await createConsentForNDHMRecords(data);

            if (consentData.consent_init_id) {
                this.setState({
                    requestCompleted: true,
                    isLoading: false,
                });
            }

            if (consentData.error) {
                notification.error({
                    message: consentData.error,
                    duration: 5,
                    placement: 'topRight',
                });
                track('ERROR', { on: 'RequestMedicalRecords', error: consentData.error });
            }
        } catch (error) {
            closeMe();
            console.log(error);
            track('ERROR', {
                on: 'RequestMedicalRecordscatch',
                error: (error as Error).toString(),
            });
        }
    };

    setStartsFrom = (startsFrom: StartsFrom) => {
        this.setState({ startsFrom: startsFrom });
    };

    setExpiry = (expiry: Expiry) => {
        this.setState({ expiry: expiry });
    };

    setPurpose = (purpose: string) => {
        this.setState({ purpose: purpose });
    };

    setRecordType = (selectedRecordTypes: string[]) => {
        this.setState({ selectedRecordTypes: selectedRecordTypes });
    };

    closeRequestModalRecords = () => {
        const { closeMe } = this.props;

        this.setState({
            startsFrom: {
                isoString: getSubtractedMonthsIsoString(6, 'months'),
                shownDate: `Last 6 months`,
            },
            expiry: {
                isoString: getAddedMonthsIsoString(6, 'months'),
                shownDate: `6 months`,
            },
            selectedRecordTypes: record_types,
            purpose: purpose_types[0].value,
            requestCompleted: false,
        });

        closeMe();
    };

    requestModalCompleted = () => {
        const { closeMe, setActiveTab, plId } = this.props;

        this.setState({
            startsFrom: {
                isoString: getSubtractedMonthsIsoString(6, 'months'),
                shownDate: `Last 6 months`,
            },
            expiry: {
                isoString: getAddedMonthsIsoString(6, 'months'),
                shownDate: `6 months`,
            },
            selectedRecordTypes: record_types,
            purpose: purpose_types[0].value,
            requestCompleted: false,
        });
        closeMe();
        his.push(GO_TO_PATIENTS_DETAILS(plId, 'abha-requests'));
    };

    onCloseWithRefresh = () => {
        const { onRefresh } = this.props;
        this.closeRequestModalRecords();
        onRefresh?.();
    };

    render() {
        const {
            startsFrom,
            expiry,
            purpose,
            selectedRecordTypes,
            requestCompleted,
            isLoading,
        } = this.state;
        const { patientDirectory, isModalOpen } = this.props;

        return (
            <Fragment>
                <Modal
                    bodyStyle={{
                        overflowY: 'auto',
                        flex: '1 1 auto',
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '0px',
                    }}
                    className="flex flex-col"
                    title={
                        <div className="font-lato w-full font-700">{'Request medical records'}</div>
                    }
                    visible={!requestCompleted && isModalOpen}
                    footer={
                        <div className="flex justify-center w-full">
                            <button
                                className="primary-btn px-12 py-8 btn btn--small space-x-8"
                                onClick={isLoading ? undefined : this.onRequestMedicalRecords}
                            >
                                {filePen}
                                <span className="w-full text-center">
                                    {isLoading ? 'Requesting...' : 'Request Medical Records'}
                                </span>
                            </button>
                        </div>
                    }
                    width={491}
                    destroyOnClose={true}
                    onCancel={this.closeRequestModalRecords}
                >
                    <div className="bg-darwin-neutral-50 py-16 px-12 select-none">
                        <div className="bg-white rounded-12 px-12 pb-28">
                            <RequestTo userName={patientDirectory?.abha?.id || ''} />
                            <RecordsFromDate
                                startsFrom={startsFrom}
                                setStartsFrom={this.setStartsFrom}
                            />
                            <RecordsExpireInDate expiry={expiry} setExpiry={this.setExpiry} />
                            <AdvancedOptions
                                purpose={purpose}
                                setPurpose={this.setPurpose}
                                selectedRecordTypes={selectedRecordTypes}
                                setRecordType={this.setRecordType}
                            />
                        </div>
                    </div>
                </Modal>
                <Modal
                    bodyStyle={{
                        overflowY: 'auto',
                        flex: '1 1 auto',
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '0px',
                    }}
                    className="flex flex-col"
                    title={
                        <div className="font-lato w-full font-700">{'Request medical records'}</div>
                    }
                    visible={requestCompleted}
                    footer={null}
                    onCancel={this.onCloseWithRefresh}
                    destroyOnClose={true}
                    width={490}
                >
                    <div className="bg-darwin-neutral-50 p-16">
                        <div className="bg-white rounded-12">
                            <RequestCompleted requestModalCompleted={this.requestModalCompleted} />
                        </div>
                    </div>
                </Modal>
            </Fragment>
        );
    }
}

const rightIcon = rightArrowIcon('fill-darwin-primary-500 w-8 h-20');
const downIcon = bottonArrowIcon('fill-darwin-primary-500  w-20 h-8');
const tick = tickIcon('fill-darwin-accent-green-800');

export const RequestTo: React.FC<{
    userName: string;
}> = ({ userName }) => {
    return (
        <div className="flex items-center space-x-8 py-12 text-14 border-b-1 border-darwin-neutral-100">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="20"
                viewBox="0 0 14 11"
                fill="none"
            >
                <path
                    d="M12.375 0.25H1.125C0.492188 0.25 0 0.765625 0 1.375V1.75H13.5V1.375C13.5 0.765625 12.9844 0.25 12.375 0.25ZM0 9.625C0 10.2578 0.492188 10.75 1.125 10.75H12.375C12.9844 10.75 13.5 10.2578 13.5 9.625V2.5H0V9.625ZM8.625 4H11.625C11.8125 4 12 4.1875 12 4.375C12 4.58594 11.8125 4.75 11.625 4.75H8.625C8.41406 4.75 8.25 4.58594 8.25 4.375C8.25 4.1875 8.41406 4 8.625 4ZM8.625 5.5H11.625C11.8125 5.5 12 5.6875 12 5.875C12 6.08594 11.8125 6.25 11.625 6.25H8.625C8.41406 6.25 8.25 6.08594 8.25 5.875C8.25 5.6875 8.41406 5.5 8.625 5.5ZM8.625 7H11.625C11.8125 7 12 7.1875 12 7.375C12 7.58594 11.8125 7.75 11.625 7.75H8.625C8.41406 7.75 8.25 7.58594 8.25 7.375C8.25 7.1875 8.41406 7 8.625 7ZM4.125 4C4.94531 4 5.625 4.67969 5.625 5.5C5.625 6.34375 4.94531 7 4.125 7C3.28125 7 2.625 6.34375 2.625 5.5C2.625 4.67969 3.28125 4 4.125 4ZM2.625 7.75H5.625C6.23438 7.75 6.75 8.26562 6.75 8.875C6.75 9.08594 6.5625 9.25 6.375 9.25H1.875C1.66406 9.25 1.5 9.08594 1.5 8.875C1.5 8.26562 1.99219 7.75 2.625 7.75Z"
                    fill="#616A8F"
                />
            </svg>
            <span className="text-darwin-neutral-900">Request to:</span>
            <span className="font-700 text-darwin-neutral-1000">{userName}</span>
        </div>
    );
};

export const RecordsFromDate: React.FC<{
    startsFrom: StartsFrom | undefined;
    setStartsFrom: (startsFrom: StartsFrom) => void;
}> = ({ startsFrom, setStartsFrom }) => {
    const [isOpen, setIsOpen] = useState(false);

    const toggle = () => {
        setIsOpen(!isOpen);
    };

    const isCellDateValue = start_from.find((from) => {
        return `Last ${from.value} ${from.unit}` === startsFrom?.shownDate;
    });

    return (
        <div className="space-x-8 py-12 text-14 border-b-1 border-darwin-neutral-100">
            <div className="flex justify-between" onClick={toggle}>
                <div className="flex items-center space-x-8">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="20"
                        viewBox="0 0 11 13"
                        fill="none"
                    >
                        <path
                            d="M9.375 2H8.25V1.25C8.25 0.851562 7.89844 0.5 7.5 0.5C7.07812 0.5 6.75 0.851562 6.75 1.25V2H3.75V1.25C3.75 0.851562 3.39844 0.5 3 0.5C2.57812 0.5 2.25 0.851562 2.25 1.25V2H1.125C0.492188 2 0 2.51562 0 3.125V4.25H10.5V3.125C10.5 2.51562 9.98438 2 9.375 2ZM0 11.375C0 12.0078 0.492188 12.5 1.125 12.5H9.375C9.98438 12.5 10.5 12.0078 10.5 11.375V5H0V11.375Z"
                            fill="#616A8F"
                        />
                    </svg>
                    <span>Request records from:</span>
                    <span className="font-700 text-darwin-neutral-1000">
                        {startsFrom?.shownDate}
                    </span>
                </div>
                {isOpen ? downIcon : rightIcon}
            </div>

            {isOpen && (
                <div className="flex flex-wrap gap-4 items-center pt-16">
                    {start_from.map((from) => {
                        const isActive =
                            `Last ${from.value} ${from.unit}` === startsFrom?.shownDate;
                        return (
                            <span
                                role="button"
                                className={`whitespace-wrap border-1 ${
                                    isActive
                                        ? 'text-darwin-primary-500  border-darwin-primary-500 bg-darwin-primary-100'
                                        : 'text-darwin-neutral-700 border-darwin-neutral-200'
                                } rounded-16 py-4 px-10 text-12 `}
                                onClick={() => {
                                    setStartsFrom({
                                        isoString: getSubtractedMonthsIsoString(
                                            from.value,
                                            from.unit,
                                        ),
                                        shownDate: `Last ${from.value} ${from.unit}`,
                                    });
                                }}
                            >
                                Last {from.value} {from.unit}
                            </span>
                        );
                    })}
                    <span
                        role="button"
                        className={`whitespace-wrap border-1 ${
                            !isCellDateValue
                                ? 'text-darwin-primary-500  border-darwin-primary-500 bg-darwin-primary-100'
                                : 'text-darwin-neutral-700 border-darwin-neutral-200'
                        } rounded-16 py-2 px-8 text-12`}
                    >
                        <DatePicker
                            picker={'date'}
                            value={
                                !isCellDateValue && startsFrom ? moment(startsFrom.isoString) : null
                            }
                            onChange={(t) => {
                                t &&
                                    setStartsFrom({
                                        isoString: t.utc(false).startOf('date').toISOString(),
                                        shownDate: t.format("DD MMM'YY"),
                                    });
                            }}
                            placeholder="Choose Dates"
                            allowClear={true}
                            bordered={false}
                            format="DD MMM'YY"
                            className={'border-none p-0'}
                        />
                    </span>
                </div>
            )}
        </div>
    );
};

export const RecordsExpireInDate: React.FC<{
    expiry: Expiry | undefined;
    setExpiry: (expiry: Expiry) => void;
}> = ({ expiry, setExpiry }) => {
    const [isOpen, setIsOpen] = useState(false);

    const toggle = () => {
        setIsOpen(!isOpen);
    };
    return (
        <div className="space-x-8 py-12 text-14 border-b-1 border-darwin-neutral-100">
            <div className="flex justify-between" onClick={toggle}>
                <div className="flex items-center space-x-8 pb-4">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="15"
                        height="20"
                        viewBox="0 0 14 11"
                        fill="none"
                    >
                        <path
                            d="M12.8594 9.27344L7.86719 0.742188C7.46875 0.0859375 6.50781 0.0859375 6.13281 0.742188L1.11719 9.27344C0.742188 9.92969 1.21094 10.75 1.98438 10.75H11.9922C12.7656 10.75 13.2344 9.92969 12.8594 9.27344ZM6.4375 3.4375C6.4375 3.13281 6.67188 2.875 7 2.875C7.30469 2.875 7.5625 3.13281 7.5625 3.4375V6.4375C7.5625 6.76562 7.30469 7 7 7C6.71875 7 6.4375 6.76562 6.4375 6.4375V3.4375ZM7 9.25C6.57812 9.25 6.25 8.92188 6.25 8.52344C6.25 8.125 6.57812 7.79688 7 7.79688C7.39844 7.79688 7.72656 8.125 7.72656 8.52344C7.72656 8.92188 7.39844 9.25 7 9.25Z"
                            fill="#616A8F"
                        />
                    </svg>
                    <span>Shared records will expire in:</span>
                    <span className="font-700 text-darwin-neutral-1000"> {expiry?.shownDate}</span>
                </div>
                {isOpen ? downIcon : rightIcon}
            </div>
            {isOpen && (
                <div className="items-center space-x-8 pt-16">
                    <p className="text-darwin-neutral-600 pb-16">
                        The expiry date of records can be changed by the patient
                    </p>
                    {expires_in.map((exp) => {
                        const isActive = `${exp.value} ${exp.unit}` === expiry?.shownDate;
                        return (
                            <span
                                role="button"
                                className={`whitespace-wrap border-1 ${
                                    isActive
                                        ? 'text-darwin-primary-500  border-darwin-primary-500 bg-darwin-primary-100'
                                        : 'text-darwin-neutral-700 border-darwin-neutral-200'
                                } rounded-16 py-6 px-10 text-12 `}
                                onClick={() => {
                                    setExpiry({
                                        isoString: getAddedMonthsIsoString(exp.value, exp.unit),
                                        shownDate: `${exp.value} ${exp.unit}`,
                                    });
                                }}
                            >
                                {exp.value} {exp.unit}
                            </span>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export const AdvancedOptions: React.FC<{
    purpose: string | undefined;
    setPurpose: (purpose: string) => void;
    selectedRecordTypes: string[] | undefined;
    setRecordType: (record: string[]) => void;
}> = ({ purpose, setPurpose, selectedRecordTypes, setRecordType }) => {
    const [isOpen, setIsOpen] = useState(false);

    const toggle = () => {
        setIsOpen(!isOpen);
    };

    const recordTypes = record_types.map((record) => ({ value: record, label: record }));

    return (
        <div className="space-x-8 py-12 text-14 border-b-1 border-darwin-neutral-100">
            <div className="flex justify-between" onClick={toggle}>
                <div className="flex items-center space-x-8">
                    <p className="font-700 text-darwin-primary-500 cursor-pointer">
                        Advanced Options:
                    </p>
                </div>
                {isOpen ? downIcon : rightIcon}
            </div>
            {isOpen && (
                <div className="flex items-center justify-between space-x-32 pt-12">
                    <div className="flex-1">
                        <p className="text-darwin-neutral-1000 mb-8">Purpose of Request</p>

                        <Select
                            className="request-mr-select max-w-176 w-full border py-2 rounded-4 font-700 text-darwin-neutral-1000"
                            value={purpose}
                            bordered={false}
                            options={purpose_types}
                            onChange={(value) => {
                                setPurpose(value);
                            }}
                        />
                    </div>
                    <div className="flex-1">
                        <p className="text-darwin-neutral-1000 mb-8">Medical record type</p>

                        <Select
                            className="request-mr-select min-w-232 w-full border py-2 rounded-4 font-700 text-darwin-neutral-1000"
                            mode="multiple"
                            maxTagCount="responsive"
                            showArrow
                            value={selectedRecordTypes}
                            bordered={false}
                            options={recordTypes}
                            onChange={(value) => {
                                setRecordType(value);
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export const RequestCompleted: React.FC<{
    requestModalCompleted?: () => void;
}> = ({ requestModalCompleted }) => {
    return (
        <div className="flex flex-col items-center py-44 px-56">
            {tick}
            <span className="text-16  mt-8 text-textColor-primary">
                Your request for medical records has been sent.
            </span>

            <div className="flex flex-col mx-20">
                <span className="py-12 text-center px-24 rounded-8 font-700 bg-darwin-accent-bright-yellow-200 mt-16 text-darwin-neutral-900">
                    You can see the medical records once the patient approves your request
                </span>
                <div className="flex-1  mt-16 justify-center w-full">
                    <button
                        className="w-full primary-btn px-12 py-8 btn btn--small space-x-8"
                        onClick={requestModalCompleted}
                    >
                        <span className="w-full text-center">Check Request Status</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RequestModalRequests;
